import { useEffect, useState } from 'react'
import * as TweetLib from 'react-tweet'
import Image from '../content/Image'
import TypographyParagraph from '../typography/TypographyParagraph'
import { AttestationType } from '#types/loreTypes.tsx'
import InternalLink from '../common/internalLink'
const { Tweet } = TweetLib

const getTweetID = (url: string): string | null =>
	url?.match(/status\/(\d+)[\/?]?/)?.[1] || null

const isValidURL = (url?: string): boolean =>
	typeof url === 'string' && /^(http|https):\/\/[^ "]+$/.test(url)

const TweetTimelineComponent = ({
	metadata,
	asCountersign = false,
	displayEmbedContent = true,
	tweetsUrl,
}: {
	metadata: AttestationType
	asCountersign: boolean
	displayEmbedContent: boolean
	tweetsUrl: string
}) => {
	const [tweet, setTweet] = useState<any>(null)
	const [tweetId] = useState<string | null>(getTweetID(metadata.properties.url))
	const [tweetError, setTweetError] = useState<boolean>(false)
	const [isFetchingTweet, setIsFetchingTweet] = useState<boolean>(false)

	useEffect(() => {
		const fetchTweet = async () => {
			if (!tweetId || tweet || tweetError || !isValidURL(tweetsUrl)) return
			setIsFetchingTweet(true)
			try {
				const response = await fetch(`${tweetsUrl}/${tweetId}`)
				const data = await response.json()
				if (data.status === 200 && data.body) setTweet(data.body)
				else setTweetError(true)
			} catch (e) {
				console.error(e)
				setTweetError(true)
			} finally {
				setIsFetchingTweet(false)
			}
		}

		if (displayEmbedContent) fetchTweet()
	}, [displayEmbedContent, tweetId, tweetsUrl, tweet, tweetError])

	return (
		<div className="flex flex-col">
			{metadata.properties.description && (
				<TypographyParagraph
					size="medium"
					isBold
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark mb-4"
				>
					{metadata.properties.description}
				</TypographyParagraph>
			)}
			{displayEmbedContent && !tweetError ? (
				isFetchingTweet ? (
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Loading tweet...
					</TypographyParagraph>
				) : (
					tweet && (
						<Tweet id={tweetId as string} onError={() => setTweetError(true)} />
					)
				)
			) : metadata.properties.web2URL ? (
				<a target="_blank" href={metadata.properties.url}
				><Image
						className="w-4/5 rounded-md"
						imageUrl={metadata.properties.web2URL}
						imageAlt={metadata.properties.url}
					/>
				</a>
			) : metadata.properties.ipfsCID ? (
				<a target="_blank" href={metadata.properties.url}
				><Image
						className="w-4/5 rounded-md"
						imageUrl={metadata.properties.ipfsCID}
						imageAlt={metadata.properties.url}
					/>
				</a>
			) : (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					This tweet cannot be displayed since it belongs to a private account
					or is flagged for sensitive content
				</TypographyParagraph>
			)}
		</div>
	)
}

export default TweetTimelineComponent
